import {
  DangerButton,
  FieldLabel,
  FormInput,
  FormInputPhone,
  Modal,
  ModalSizeEnum,
  NeutralButton,
  PrimaryButton,
  RadioGroup,
} from '@systemeio/ui'
import { eventLocationLocoKeys } from 'modules/calendar/constants/calendar-loco'
import { LocationOnlineMeetingModal } from 'modules/calendar/events/components/location-online-meeting-modal'
import { EventTypeEnum } from 'modules/calendar/events/enums/event-type-enum'
import { hasMeetingLink } from 'modules/calendar/events/utils/online-meeting-utils'
import { useCalendars } from 'modules/profile/integrations/google/hooks/use-calendars'
import React, { PropsWithChildren, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { getUserProfileInfo } from 'shared/api/user-api'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { isValidUrl } from 'shared/utils/is-valid-url'
import { twMerge } from 'tailwind-merge'
import { EventLocationTypeEnum, linkTypes, locationFields } from '../constants'
import { useCheckZoomIntegration } from '../hooks/use-check-zoom-integration'
import {
  EventCreateFormInterface,
  EventEditFormInterface,
  EventLocationInterface,
} from '../types/event-interface'
import { LocationIcon, PhoneIcon, ZoomIcon } from '@systemeio/ui/icons'

interface LocationTypeFieldsProps {
  locations: EventLocationInterface[]
  setValue: UseFormSetValue<EventCreateFormInterface | EventEditFormInterface>
  error?: string
  eventType: EventTypeEnum
}

const locationTypeButtons = Object.values(EventLocationTypeEnum).filter(
  type => !hasMeetingLink(type),
)

type TempLocationState = EventCreateFormInterface['locations'][number]

const validationRules = {
  [EventLocationTypeEnum.ExternalLink]: 'meetingLink',
  [EventLocationTypeEnum.Personal]: 'address',
  [EventLocationTypeEnum.Phone]: 'phone',
}

export function LocationTypeFields({
  locations,
  setValue,
  error,
  eventType,
}: LocationTypeFieldsProps) {
  const { t } = useLocoTranslation()
  const [errors, setErrors] = useState({ phone: '', meetingLink: '', address: '' })

  const { isZoomIntegrated, isLoading: isZoomIntegratedLoading } = useCheckZoomIntegration()
  const { hasGoogleCalendars, isLoading: isGoogleCalendarsLoading } = useCalendars()
  const isLoading = isGoogleCalendarsLoading || isZoomIntegratedLoading

  const [tempState, setTempState] = useState<TempLocationState | null>(null)
  const [opened, setOpened] = useState(false)

  const isMeetingLinkSelected = hasMeetingLink(tempState?.locationType)

  const openLocationModal = (locationType: EventLocationTypeEnum) => {
    const isLinkLocationType = hasMeetingLink(locationType)
    const isPhoneLocationType = hasPhoneField(locationType)
    setOpened(true)

    if (isPhoneLocationType) {
      getUserProfileInfo().then(res => {
        tempState &&
          setTempState(
            prev => ({ ...prev, phone: prev?.phone || res.data.phone } as TempLocationState),
          )
      })
    }

    setTempState(prev => {
      if (isLinkLocationType) {
        const savedLocationType = locations.find(location => hasMeetingLink(location.locationType))
        if (savedLocationType) {
          return {
            ...prev,
            ...savedLocationType,
          }
        } else {
          // set Zoom as default value for externalLink location modal
          const defaultLocationType = isZoomIntegrated ? EventLocationTypeEnum.Zoom : locationType
          return {
            ...prev,
            ...locationFields[defaultLocationType],
            locationType: defaultLocationType,
          }
        }
      }

      const savedLocationType = locations.find(location => location.locationType === locationType)
      return {
        ...prev,
        ...locationFields[locationType],
        locationType,
        ...savedLocationType,
      }
    })
  }

  const filterLocationBasedOnTempState = (locations: EventLocationInterface[]) => {
    if (!tempState) return locations

    let newLocations = locations

    if (isOnlineMeetingLocation(tempState.locationType)) {
      newLocations = locations.filter(location => !isOnlineMeetingLocation(location.locationType))
    } else if (isPersonalLocation(tempState.locationType)) {
      newLocations = locations.filter(location => !isPersonalLocation(location.locationType))
    } else if (isPhoneLocation(tempState.locationType)) {
      newLocations = locations.filter(location => !isPhoneLocation(location.locationType))
    }

    return newLocations
  }

  const deleteLocation = () => {
    setValue('locations', filterLocationBasedOnTempState(locations))
    setOpened(false)
  }

  const onSubmit = () => {
    if (tempState) {
      const missingField = validationRules[tempState.locationType as keyof typeof validationRules]
      if (missingField && !tempState[missingField as keyof typeof tempState]) {
        setErrors(prev => ({
          ...prev,
          [missingField]: t('dashboard.validation.empty_error.title'),
        }))
        return
      }

      if (
        tempState.locationType === EventLocationTypeEnum.ExternalLink &&
        !isValidUrl(tempState.meetingLink!)
      ) {
        setErrors(prev => ({
          ...prev,
          meetingLink: t('dashboard.calendar.meeting_link_validation.title'),
        }))
        return
      }

      if (eventType === EventTypeEnum.Group) {
        setValue('locations', [tempState])
      } else {
        setValue('locations', [...filterLocationBasedOnTempState(locations), tempState])
      }
    }
    setOpened(false)
  }

  return (
    <div className="order-2 sm:order-1">
      <FieldLabel required label={t('dashboard.calendar.location')} />
      <div className="flex flex-wrap gap-4">
        {/* single button for locations with the link */}
        {eventType === EventTypeEnum.Group ? (
          <LocationButton
            locationType={EventLocationTypeEnum.ExternalLink}
            openLocationModal={openLocationModal}
            isFetching={isLoading}
            isSelected={locations.some(({ locationType }) => hasMeetingLink(locationType))}
          >
            <span className="flex flex-col items-center justify-center gap-3 ">
              <LocationTypeIcon locationType={EventLocationTypeEnum.ExternalLink} />
              {t(eventLocationLocoKeys[EventLocationTypeEnum.ExternalLink])}
            </span>
          </LocationButton>
        ) : (
          <LocationOnlineMeetingModal locations={locations} error={error} setValue={setValue} />
        )}

        {locationTypeButtons.map(locationType => (
          <LocationButton
            key={locationType}
            locationType={locationType}
            openLocationModal={openLocationModal}
            isFetching={isLoading}
            isSelected={locations.some(x => x.locationType === locationType)}
          >
            <span className="flex flex-col items-center justify-center gap-3">
              <LocationTypeIcon locationType={locationType} />
              {t(eventLocationLocoKeys[locationType])}
            </span>
          </LocationButton>
        ))}
      </div>
      <FieldErrorAndDescription className="mt-2" error={error} />

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={tempState && t(eventLocationLocoKeys[tempState.locationType])}
        size={ModalSizeEnum.small}
        afterLeave={() => setTempState(null)}
      >
        {isMeetingLinkSelected && (
          <div className="flex flex-col gap-5">
            <RadioGroup
              optionsWrapperClassName="items-start flex flex-col"
              options={linkTypes
                .map(({ id, caption }) => {
                  if (id === EventLocationTypeEnum.GoogleMeet) {
                    return {
                      id,
                      caption: t(caption),
                      tooltipLabel: t(
                        'dashboard.calendar.event_inactive_google_meet_location_error',
                      ),
                      showTooltip: !hasGoogleCalendars,
                      disabled: !hasGoogleCalendars,
                    }
                  }

                  if (id === EventLocationTypeEnum.Zoom) {
                    return {
                      id,
                      caption: t(caption),
                      tooltipLabel: t('dashboard.calendar.event_inactive_zoom_location_error'),
                      showTooltip: !isZoomIntegrated,
                      disabled: !isZoomIntegrated,
                    }
                  }

                  return {
                    id,
                    caption: t(caption),
                  }
                })
                .filter(Boolean)}
              selected={tempState?.locationType}
              onChange={val => {
                setTempState(
                  prev => prev && { ...prev, locationType: val as EventLocationTypeEnum },
                )
              }}
            />
            {tempState?.locationType === EventLocationTypeEnum.ExternalLink && (
              <FormInput
                value={tempState?.meetingLink || ''}
                onChange={e => {
                  if (errors.meetingLink) setErrors(prev => ({ ...prev, meetingLink: '' }))
                  setTempState(prev => prev && { ...prev, meetingLink: e.target.value })
                }}
                label={t('dashboard.calendar.event_location_meeting_link')}
                required
                error={errors.meetingLink}
              />
            )}
          </div>
        )}

        {tempState?.locationType === EventLocationTypeEnum.Phone && (
          <FormInputPhone
            label={t('dashboard.calendar.event_location.phone')}
            value={tempState?.phone}
            onChange={value => {
              if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }))
              setTempState(prev => prev && { ...prev, phone: value })
            }}
            maxLength={20}
            required
            error={errors.phone}
          />
        )}

        {tempState?.locationType === EventLocationTypeEnum.Personal && (
          <div className="flex flex-col gap-5">
            <FormInput
              name="location.address"
              value={tempState.address || ''}
              onChange={e => {
                if (errors.address) setErrors(prev => ({ ...prev, address: '' }))
                setTempState(prev => prev && { ...prev, address: e.target.value })
              }}
              label={t('dashboard.calendar.event_location_address')}
              required
              error={errors.address}
            />
            <FormInputPhone
              label={t('dashboard.calendar.event_location.phone')}
              value={tempState.phone}
              onChange={value => {
                setTempState(prev => prev && { ...prev, phone: value })
              }}
              maxLength={20}
            />
          </div>
        )}

        <div className="mt-6 flex gap-4 lg:mt-10">
          <DangerButton
            className="w-full"
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              deleteLocation()
            }}
          >
            {t('global.delete')}
          </DangerButton>
          <PrimaryButton
            className="w-full"
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              onSubmit()
            }}
          >
            {t('global.save')}
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  )
}

interface LocationButtonProps extends PropsWithChildren {
  locationType: EventLocationTypeEnum
  openLocationModal: (locationType: EventLocationTypeEnum) => void
  isSelected: boolean
  isFetching?: boolean
}

export const LocationButton = ({
  locationType,
  openLocationModal,
  isSelected,
  isFetching,
  children,
}: LocationButtonProps) => (
  <NeutralButton
    type="button"
    key={locationType}
    isFetching={isFetching}
    className={twMerge(
      'h-28 w-32 rounded-md border md:h-32 md:w-36',
      twMerge(isSelected && 'border-brand-primary [&>div>span>svg]:fill-brand-primary outline-2'),
    )}
    onClick={() => {
      openLocationModal(locationType)
    }}
  >
    {children}
  </NeutralButton>
)

export function LocationTypeIcon({ locationType }: { locationType: EventLocationTypeEnum }) {
  switch (locationType) {
    case EventLocationTypeEnum.ExternalLink:
      return <ZoomIcon />
    case EventLocationTypeEnum.Phone:
      return <PhoneIcon />
    case EventLocationTypeEnum.Personal:
      return <LocationIcon />
    default:
      return null
  }
}

function hasPhoneField(locationType?: EventLocationTypeEnum) {
  return (
    !!locationType &&
    [EventLocationTypeEnum.Personal, EventLocationTypeEnum.Phone].includes(locationType)
  )
}

function isOnlineMeetingLocation(locationType?: EventLocationTypeEnum) {
  return (
    !!locationType &&
    [
      EventLocationTypeEnum.ExternalLink,
      EventLocationTypeEnum.Zoom,
      EventLocationTypeEnum.GoogleMeet,
    ].includes(locationType)
  )
}

function isPhoneLocation(locationType?: EventLocationTypeEnum) {
  return EventLocationTypeEnum.Phone === locationType
}

function isPersonalLocation(locationType?: EventLocationTypeEnum) {
  return EventLocationTypeEnum.Personal === locationType
}
